import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

interface PublicRouteProps {
  children?: React.ReactNode;
  redirectTo?: string;
}

export default function PublicRoute({
  children,
  redirectTo = '/dashboard'
}: PublicRouteProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        let currentUser;
        try {
          currentUser = await getCurrentUser();
        } catch {
          currentUser = null;
        }

        if (!isMounted) return;

        const isAuthenticated = !!currentUser;

        setAuthChecked(true);
        setIsLoading(false);

        if (isAuthenticated) {
          const mfaVerified = sessionStorage.getItem('mfa_verified') === 'true';
          // On /login, if user is staff and MFA is not yet verified, do NOT redirect to dashboard
          if (location.pathname === '/login' && !mfaVerified) {
            return;
          }

          const isAuthPage = ['/login', '/auth/register', '/auth/forgot-password', '/verify-email'].includes(location.pathname);
          if (isAuthPage) {
            navigate(redirectTo, { replace: true });
            return;
          }
        }

      } catch (error) {
        console.error('Auth check error:', error);
        if (isMounted) {
          setAuthChecked(true);
          setIsLoading(false);
        }
      }
    };

    checkAuth();

    const hubListener = Hub.listen('auth', ({ payload }) => {
      if (!isMounted) return;

      // Nuclear guard: block everything if we're on forgot-password
      if (window.location.pathname.includes('forgot-password')) {
        return;
      }

      const { event } = payload;
      setAuthChecked(true);
      setIsLoading(false);
      if (event === 'signedIn') {
        // Do NOT redirect /login on signedIn because Login component handles staff MFA OTP flow!
        const isAuthPage = ['/auth/register', '/auth/forgot-password', '/verify-email'].includes(location.pathname);
        if (isAuthPage) {
          navigate(redirectTo, { replace: true });
        }
      }
    });

    return () => {
      isMounted = false;
      hubListener();
    };
  }, [location.pathname, navigate, redirectTo]);

  // Show loading state briefly
  if (isLoading || !authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // For public routes, always render the children
  return <>{children}</>;
}